import React, {createElement, useMemo} from "react";
import {MonthlyOfficeVisitReport, useVisitWithStatStore} from "../../../api/VisitWithStatStore";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {KeyData} from "../../../api/Model";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {Link as RouterLink} from "react-router-dom";
import {KeyChip} from "../../../components/key-chip/KeyChip";
import {Box, Typography} from "@mui/material";
import {numberCmp} from "../../../util/SortUtil";
import {monthName} from "../../../util/DateUtil";
import {locationTypeOptions} from "../../../components/location/KeyLocationShield";
import {Desks_Read, KeyData_Read, VisitReports_Read} from "../../../util/access/Permission";
import {KeyHistoryButton} from "../../history-dialog/HistoryDialogContext";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {associateBy} from "../../../util/MapUtil";

type GhostKeyProblem = {
    keyNumber: string;
    keyData?: KeyData;
    months: Map<string, number>;
    locationTypes: Set<'with-vault' | 'lost' | 'damaged' | 'deactivated'>;
    usageCount: number;
};

const ghostKeysCsvModel = (reports: MonthlyOfficeVisitReport[]) => new CsvModel<GhostKeyProblem>([
    {header: 'Key', value: problem => problem.keyNumber},
    {header: 'Known Key?', value: problem => problem.keyData ? 'Yes' : 'No'},
    {
        header: 'Locations',
        value: problem => Array.from(problem.locationTypes)
            .map(locationType => locationTypeOptions[locationType].label)
            .join(', '),
    },
    ...reports.map(report => ({
        header: report.month,
        value: (problem: GhostKeyProblem) => String(problem.months.get(report.month) ?? 0)
    })),
    {header: 'Total', value: problem => String(problem.usageCount)},
]);

export function GhostKeysWidget() {
    const pageUrl = usePageUrl();
    const reports = useVisitWithStatStore(state => state.remoteValue);
    const relevantReports = useMemo(() =>
        reports.map(reports =>
            [...reports.slice(0, 4)].reverse()
        ), [reports]
    );
    const problems = useMemo(() => relevantReports
        .map(relevantReports => {
                const problemsByKey = new Map<string, GhostKeyProblem>();
                const baseMonthlyUsages = associateBy(
                    relevantReports,
                    report => report.month,
                    () => 0,
                );
                relevantReports.forEach(report => {
                    return report.validation.ghostUsages.forEach(usage => {
                        const problem = problemsByKey.get(usage.keyNumber) || {
                            keyNumber: usage.keyNumber,
                            keyData: usage.keyData,
                            months: new Map<string, number>(baseMonthlyUsages),
                            locationTypes: new Set(),
                            usageCount: 0,
                        };
                        problem.months.set(report.month, usage.usages.length);
                        problem.usageCount += usage.usages.length;
                        usage.usages
                            .flatMap(usage => usage.type === 'ghost' ? usage.locationTypes : [])
                            .forEach(locationType => problem.locationTypes.add(locationType));
                        problemsByKey.set(usage.keyNumber, problem);
                    });
                });
                return Array
                    .from(problemsByKey.values())
                    .sort(numberCmp(problem => problem.usageCount, true));
            }
        ), [relevantReports]);

    const csvModel = useMemo(() =>
        relevantReports.map(
            relevantReports => ghostKeysCsvModel(relevantReports)
        ),
        [relevantReports],
    );

    return (
        <DashboardWidget
            title="Ghost key usages"
            description="Key usages reported while the key was not issued to an employee or guest"
            problems={problems}
            requiredPermissions={[Desks_Read, KeyData_Read, VisitReports_Read]}
            link={pageUrl(AppPages.visitReports)}
            csvModel={csvModel}
            renderProblem={(problem, index) => (
                <DashboardWidgetItem
                    key={problem.keyNumber}
                    index={index}
                    left={problem.keyData ? (
                        <>
                            <RouterLink to={pageUrl(AppPages.keys, {}, {search: problem.keyNumber})}>
                                <KeyChip keyData={problem.keyData}/>
                            </RouterLink>
                            <KeyHistoryButton keyData={problem.keyData}/>
                        </>
                    ) : (
                        <Typography>{problem.keyNumber} (not registered)</Typography>
                    )}
                    right={
                        <>
                            {Array.from(problem.locationTypes)
                                .map(locationType =>
                                    locationType === 'with-vault' ? (
                                        locationTypeOptions["with-vault"]
                                    ) : locationType === 'lost' ? (
                                        locationTypeOptions["lost"]
                                    ) : locationType === 'damaged' ? (
                                        locationTypeOptions["damaged"]
                                    ) : (
                                        locationTypeOptions["deactivated"]
                                    )
                                )
                                .map(option =>
                                    <Box key={option.label} title={option.label}>{
                                        createElement(option.icon, {
                                            color: 'disabled',
                                            sx: {fontSize: '16px'},
                                        })
                                    }</Box>
                                )
                            }
                            <Typography variant="body2" color="text.secondary">
                                {problem.usageCount} usages · {
                                    (Array.from(problem.months.entries()) as [string, number][])
                                        .filter(([_, count]) => count > 0)
                                        .map(([month, _]) => monthName(month, 'MMM'))
                                        .join(', ')
                                }
                            </Typography>
                        </>
                    }
                />
            )}
        />
    );
}
